var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { Row, Col, Button, MessageBanner } from "@folio/stripes/components";
import { FormattedMessage } from "react-intl";
import { useFieldArray, useFormContext } from "react-hook-form";
import { arrayMove } from "@dnd-kit/sortable";
import { useContributionEditor } from "../../hooks/title/useContributionEditor";
import { buildPersonOptionLabel, } from "../../hooks/title/usePersonOptions";
import { ContributionFormModal } from "./ContributionFormModal";
import { EntityRowList } from "./EntityRowList";
import rowCss from "./EntityRow.module.css";
import css from "./TitleContributionsForm.module.css";
var detectPersonType = function (item) {
    if (item.unnamedPerson)
        return "unnamed";
    if (item.person && "name" in item.person)
        return "legal";
    return "natural";
};
var TitleContributionsForm = function (_a) {
    var serverError = _a.serverError, onClearServerError = _a.onClearServerError;
    var control = useFormContext().control;
    var _b = useFieldArray({ control: control, name: "titleContributions" }), contributionFields = _b.fields, appendContribution = _b.append, updateContribution = _b.update, replaceContributions = _b.replace;
    var resequence = function (fields) {
        return fields.map(function (f, i) { return (__assign(__assign({}, f), { sequenceNumber: i + 1 })); });
    };
    var handleDelete = function (index) {
        var updated = resequence(contributionFields.filter(function (_, i) { return i !== index; }));
        replaceContributions(updated);
    };
    var _c = useContributionEditor({
        existingCount: contributionFields.length,
        appendContribution: appendContribution,
        updateContribution: updateContribution,
    }), showContributionForm = _c.showContributionForm, editingContributionIndex = _c.editingContributionIndex, contributionFormData = _c.contributionFormData, setContributionFormData = _c.setContributionFormData, openContributionForm = _c.openContributionForm, closeContributionForm = _c.closeContributionForm, saveContribution = _c.saveContribution, addContributionRole = _c.addContributionRole, removeContributionRole = _c.removeContributionRole, updateContributionRole = _c.updateContributionRole;
    var _d = React.useState("natural"), initialPersonType = _d[0], setInitialPersonType = _d[1];
    var getPersonLabel = function (item) {
        var _a, _b, _c;
        // Prefer the shared person label (adds birth–death years for natural
        // persons, exactly like the person picker) when the linked person is
        // populated; otherwise fall back to the contribution's own snapshot.
        if ((_a = item.person) === null || _a === void 0 ? void 0 : _a.legalForm) {
            var label = buildPersonOptionLabel(item.person);
            if (label)
                return label;
        }
        if (item.firstName || item.lastName) {
            return "".concat(item.firstName || "", " ").concat(item.lastName || "").trim();
        }
        if ((_b = item.unnamedPerson) === null || _b === void 0 ? void 0 : _b.description) {
            return (_c = item.unnamedPerson) === null || _c === void 0 ? void 0 : _c.description;
        }
        return "-";
    };
    var getRolesLabel = function (item) {
        var _a, _b;
        return (_b = (_a = item.roles) === null || _a === void 0 ? void 0 : _a.map(function (r) { var _a; return (_a = r.role) === null || _a === void 0 ? void 0 : _a.description; }).filter(Boolean).join(", ")) !== null && _b !== void 0 ? _b : "";
    };
    var contentData = contributionFields.map(function (field, index) { return (__assign(__assign({}, field), { _rhfId: field.id, _index: index })); });
    var handleReorder = function (from, to) {
        var reordered = arrayMove(contributionFields, from, to);
        replaceContributions(resequence(reordered));
    };
    var handleOpenContributionForm = function (item, index) {
        onClearServerError === null || onClearServerError === void 0 ? void 0 : onClearServerError();
        setInitialPersonType(item ? detectPersonType(item) : "natural");
        openContributionForm(item, index);
    };
    var renderContribution = function (row) {
        var _a;
        var roles = getRolesLabel(row);
        return (React.createElement(React.Fragment, null,
            React.createElement("div", null,
                React.createElement("span", { className: rowCss.entryPrimary }, (_a = row.sequenceNumber) !== null && _a !== void 0 ? _a : row._index + 1,
                    ". ",
                    getPersonLabel(row)),
                roles && React.createElement("span", { className: rowCss.entryRole },
                    " (",
                    roles,
                    ")")),
            row.biographicalNote && (React.createElement("div", { className: rowCss.entrySecondary }, row.biographicalNote))));
    };
    return (React.createElement(React.Fragment, null,
        serverError && (React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(MessageBanner, { type: "error" }, serverError)))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 }, contributionFields.length > 0 ? (React.createElement(EntityRowList, { data: contentData, renderContent: renderContribution, onEdit: function (row) { return handleOpenContributionForm(row, row._index); }, onDelete: function (row) { return handleDelete(row._index); }, getId: function (row) { return row._rhfId; }, onReorder: handleReorder })) : (React.createElement("div", { className: css.emptyState },
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.contributions.empty" }))))),
        React.createElement(ContributionFormModal, { open: showContributionForm, isEditing: editingContributionIndex !== null, draft: contributionFormData, onDraftChange: setContributionFormData, initialPersonType: initialPersonType, onSave: saveContribution, onClose: closeContributionForm, onAddRole: addContributionRole, onRemoveRole: removeContributionRole, onUpdateRole: updateContributionRole }),
        React.createElement(Row, { className: css.actions },
            React.createElement(Col, { xs: 12 },
                React.createElement(Button, { buttonStyle: "primary", onClick: function () { return handleOpenContributionForm(); } },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.title.form.add.contribution" }))))));
};
export default TitleContributionsForm;
